import React from 'react';
import { Helmet } from 'react-helmet-async';
import MainLayout from '../layouts/MainLayout';
import { WarningOutlined, SafetyOutlined, ToolOutlined, CheckCircleOutlined, ThunderboltOutlined, CarOutlined } from '@ant-design/icons';
import { Alert, Timeline, Card } from 'antd';

const EVTireGuide = () => {
    const safetyWarnings = [
        {
            icon: '⚡',
            title: 'Şarj Kablosu Uyarısı',
            description: 'Şarj işlemi devam etmese bile, şarj kablosu bağlıyken elektrikli aracı asla kaldırmayın.'
        },
        {
            icon: '⚠️',
            title: 'Kaldırma Noktası',
            description: 'Yanlış kaldırma noktasının veya yan rayların altından kaldırmayın. Bu pil hasarına, kişisel yaralanmaya veya ölüme neden olabilir.'
        },
        {
            icon: '🚫',
            title: 'Yanlış Destek',
            description: 'Yanlış desteklenen bir araç üzerinde çalışmayın. Araç seviyesinin, eğiminin ve uygun konumunun kontrol edilmesi şarttır.'
        },
        {
            icon: '🔥',
            title: 'Çalışma Alanı',
            description: 'Alanın temiz olduğundan emin olun. Yangın malzemelerini ve tehlikeli maddeleri uzaklaştırın.'
        }
    ];

    const procedureSteps = [
        {
            step: 1,
            title: 'Başlangıç Hazırlığı',
            description: 'Aracı düz bir yüzeye park edin, el frenini çekin ve vitesi park konumuna alın.',
            details: [
                'Motor ve şarj sistemini tamamen kapatın',
                'Şarj kablosunun bağlı olmadığından emin olun',
                'Aracın seviye ve sabit olduğunu kontrol edin',
                'Güvenlik ekipmanlarınızı (eldiven, gözlük) takın'
            ],
            icon: <CheckCircleOutlined className="text-3xl text-blue-500" />
        },
        {
            step: 2,
            title: 'Araç Konumu Kurulumu',
            description: 'Aracı merkezi olarak yerleştirin ve çalışma alanını hazırlayın.',
            details: [
                'Aracın etrafında yeterli çalışma alanı bırakın',
                'Trafik güvenliği için reflektör ve uyarı işaretleri koyun',
                'Zeminin sağlam ve düz olduğunu kontrol edin',
                'Yangın söndürücünün yakında olduğundan emin olun'
            ],
            icon: <CarOutlined className="text-3xl text-green-500" />
        },
        {
            step: 3,
            title: 'Belirlenmiş Kaldırma Noktalarını Kullanın',
            description: 'Araç üreticisinin belirlediği kaldırma noktalarını kullanmak kritik önem taşır.',
            details: [
                'Kullanım kılavuzunda belirtilen noktalara bakın',
                'Kaldırma noktaları genellikle şase rayları üzerindedir',
                'Kriko kauçuğunu belirlenen gövde kaldırma noktalarının altına yerleştirin',
                'Pil paketi ve elektrik kablolarından uzak durun'
            ],
            icon: <ToolOutlined className="text-3xl text-orange-500" />
        },
        {
            step: 4,
            title: 'Kaldırma Prosedürü',
            description: 'Aracı yavaş ve kontrollü bir şekilde kaldırın.',
            details: [
                'Krikoyu yavaş ve düzgün bir şekilde çalıştırın',
                'Araç dengeli bir şekilde yükseldiğinden emin olun',
                'Kaldırma sırasında aracın altına girmeyin',
                'Destek standları (jack stand) kullanarak aracı sabitleyin'
            ],
            icon: <SafetyOutlined className="text-3xl text-red-500" />
        },
        {
            step: 5,
            title: 'Lastik Değişimi',
            description: 'Lastikleri güvenli bir şekilde değiştirin ve doğru hava basıncını ayarlayın.',
            details: [
                'Bijon somunlarını yıldız şeklinde gevşetin',
                'Eski lastiği çıkarın ve yeni lastiği takın',
                'Bijon somunlarını çapraz sırayla sıkın',
                'Elektrikli araç için önerilen hava basıncını ayarlayın (genellikle 2.5-3.0 bar)',
                'Tork anahtarı ile bijonları üretici değerinde sıkın'
            ],
            icon: <ThunderboltOutlined className="text-3xl text-purple-500" />
        }
    ];

    const evTireFeatures = [
        {
            title: 'Yüksek Yük Kapasitesi',
            description: 'EV lastikleri, ağır batarya paketlerini taşımak için güçlendirilmiş yapıya sahiptir.',
            icon: '💪'
        },
        {
            title: 'Düşük Yuvarlanma Direnci',
            description: 'Enerji tasarrufu ve daha uzun menzil için optimize edilmiş dizayn.',
            icon: '⚡'
        },
        {
            title: 'Sessiz Yapı',
            description: 'Motor sesi olmadığı için yol gürültüsünü minimize eden özel teknoloji.',
            icon: '🔇'
        },
        {
            title: 'Yüksek Tork Dayanımı',
            description: 'Elektrikli araçların anında yüksek torka karşı dayanıklı yapı.',
            icon: '🚀'
        }
    ];

    return (
        <MainLayout>
            <Helmet>
                <title>Elektrikli Araç Lastiği Değiştirme Rehberi - Sakarya Lastik</title>
                <meta name="description" content="Elektrikli araç lastiği değiştirme önlemleri, güvenlik uyarıları ve adım adım prosedürler. Tesla, BMW i3, Mercedes EQC için lastik değişim rehberi." />
                <meta name="keywords" content="elektrikli araç lastiği, EV lastik değişimi, tesla lastik, elektrikli araç bakımı, ev tire safety" />
            </Helmet>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16 m-16">
                <div className="container mx-auto px-4">
                    <div className="flex items-center mb-4">
                        <ThunderboltOutlined className="text-6xl mr-4" />
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-2">
                                Elektrikli Araç Lastiği Değiştirme Rehberi
                            </h1>
                            <p className="text-xl text-purple-100">
                                Güvenli ve doğru lastik değişimi için profesyonel kılavuz
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Introduction */}
            <div className="container mx-auto px-4 py-12">
                <Alert
                    message="Önemli Bilgilendirme"
                    description="Elektrikli araçların kendine özgü iç yapıları nedeniyle doğru kaldırma yöntemlerinin takip edilmesi ve lastik değiştirme prosedürleri, ciddi hasarları veya güvenlik olaylarını önlemek için çok önemlidir."
                    type="warning"
                    icon={<WarningOutlined />}
                    showIcon
                    className="mb-12"
                />

                {/* Why It's Important */}
                <div className="bg-blue-50 rounded-lg p-8 mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                        <SafetyOutlined className="text-4xl text-blue-600 mr-3" />
                        Elektrikli Araçlar İçin Doğru Kaldırmanın Önemi
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-xl font-bold text-gray-800 mb-3">⚡ Pil Paketi Koruması</h3>
                            <p className="text-gray-700">
                                Elektrikli araçlarda pil paketi aracın alt kısmında bulunur. Yanlış kaldırma,
                                pil hasarına ve yangın riskinin artmasına neden olabilir. Pil hasarı çok maliyetli
                                olup garanti kapsamı dışındadır.
                            </p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-xl font-bold text-gray-800 mb-3">🔌 Elektrik Sistemi Güvenliği</h3>
                            <p className="text-gray-700">
                                Yüksek voltajlı elektrik kabloları ve bileşenler hasar görebilir. Yanlış kaldırma
                                elektrik şoku, sistem arızası veya toplam araç hasarına yol açabilir.
                            </p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-xl font-bold text-gray-800 mb-3">⚖️ Ağırlık Dağılımı</h3>
                            <p className="text-gray-700">
                                Elektrikli araçlar geleneksel araçlardan %20-30 daha ağırdır. Standart krikolar
                                yeterli olmayabilir, araç özel kriko ve destek noktaları gerektirir.
                            </p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-xl font-bold text-gray-800 mb-3">📋 Garanti Koruması</h3>
                            <p className="text-gray-700">
                                Araç üreticisine göre yanlış kaldırmadan kaynaklanan hasarlar garanti kapsamında
                                değildir. Üretici prosedürlerine uygun hareket etmek şarttır.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Safety Warnings */}
                <div className="bg-red-50 rounded-lg p-8 mb-12">
                    <h2 className="text-3xl font-bold text-red-700 mb-6 flex items-center">
                        <WarningOutlined className="text-4xl mr-3" />
                        Kritik Güvenlik Uyarıları
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {safetyWarnings.map((warning, index) => (
                            <div key={index} className="bg-white border-l-4 border-red-500 p-6 rounded shadow-md">
                                <div className="flex items-start">
                                    <span className="text-4xl mr-4">{warning.icon}</span>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                                            {warning.title}
                                        </h3>
                                        <p className="text-gray-700">
                                            {warning.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step by Step Procedure */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                        Adım Adım Lastik Değiştirme Prosedürü
                    </h2>
                    <Timeline mode="left">
                        {procedureSteps.map((step, index) => (
                            <Timeline.Item
                                key={index}
                                dot={step.icon}
                                color="blue"
                            >
                                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                                    <div className="flex items-center mb-3">
                                        <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center mr-3 font-bold text-lg">
                                            {step.step}
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-800">
                                            {step.title}
                                        </h3>
                                    </div>
                                    <p className="text-gray-700 mb-4 text-lg">
                                        {step.description}
                                    </p>
                                    <ul className="space-y-2">
                                        {step.details.map((detail, idx) => (
                                            <li key={idx} className="flex items-start text-gray-700">
                                                <CheckCircleOutlined className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                                                <span>{detail}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>

                {/* EV Tire Features */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-8 mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                        Elektrikli Araç Lastiklerinin Özellikleri
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {evTireFeatures.map((feature, index) => (
                            <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-shadow">
                                <div className="text-5xl mb-4">{feature.icon}</div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Professional Service CTA */}
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg p-12 text-center">
                    <ThunderboltOutlined className="text-6xl mb-4" />
                    <h2 className="text-3xl font-bold mb-4">
                        Elektrikli Aracınız İçin Profesyonel Hizmet
                    </h2>
                    <p className="text-xl text-gray-300 mb-6 max-w-3xl mx-auto">
                        Tesla, BMW i3, Mercedes EQC, Volkswagen ID.3/4 ve tüm elektrikli araçlar için
                        uzman kadromuz ve özel ekipmanlarımızla güvenli lastik değişimi yapıyoruz.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="tel:+905441205454"
                            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
                        >
                            📞 Hemen Arayın: 0544 120 54 54
                        </a>
                        <a
                            href="/lastik"
                            className="bg-white text-gray-800 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
                        >
                            ⚡ EV Lastiklerini İnceleyin
                        </a>
                    </div>
                </div>

                {/* Important Notes */}
                <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <WarningOutlined className="text-yellow-600 mr-2" />
                        Önemli Notlar
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                        <li>• Araç üreticisine göre yanlış kaldırmadan kaynaklanan hasarlar garanti kapsamında değildir.</li>
                        <li>• Ayrıntılı prosedürler (araca özel kaldırma noktaları dahil) üreticiye göre değişebilir, bu nedenle lütfen araç kullanım kılavuzuna bakınız.</li>
                        <li>• Elektrikli araç lastik değişiminde kendinize güvenmiyorsanız, mutlaka profesyonel yardım alın.</li>
                        <li>• Yüksek voltaj uyarı etiketlerinin bulunduğu yerlere dokunmayın.</li>
                        <li>• Elektrikli araç özel kriko ve kaldırma ekipmanları kullanın.</li>
                    </ul>
                </div>
            </div>
        </MainLayout>
    );
};

export default EVTireGuide;
